import { minorUnitToMajorUnit } from "@repo/system";
import { type TransactionItem } from "~/types/events";
import { type KioskDisplayBasketItem } from "~/stores/kiosk-session/hooks/use-kiosk-products/types";
import { TRANSACTION_CODE } from "~/utils/constants";
import { roundAwayFromZero } from "~/utils/round-away-from-zero";

const SKU_MAX_LENGTH = 30;
const TEXT_MAX_LENGTH = 500;
const FALLBACK_PRODUCT_NAME = "Item";

const toFreewayText = (value: string, maxLength: number) =>
  value.replace(/\s+/g, " ").trim().slice(0, maxLength);

/**
 * Maps the kiosk basket to FreedomPay line items.
 *
 * Every money field is a line total, not a per-unit price: Freeway reads
 * `taxAmount` as the tax contained within `totalAmount` (`taxIncludedFlag`).
 * A quantity-less line keeps its place in the payload so the lines still sum
 * to the amount charged; only the division is guarded.
 *
 * A deposit rides on its product's line rather than taking one of its own: a
 * line is identified only by `productSKU`, and a deposit has no product id to
 * put there.
 */
export function toFreedomPayItems(
  basket: KioskDisplayBasketItem[]
): TransactionItem[] {
  return basket.map((item) => {
    const productName =
      toFreewayText(item.product.name, TEXT_MAX_LENGTH) ||
      FALLBACK_PRODUCT_NAME;

    const productDescription =
      toFreewayText(item.product.description ?? "", TEXT_MAX_LENGTH) ||
      productName;

    const lineTotal = item.unitTotalPriceInclVat + item.depositTotalInclVat;
    const depositTax = item.depositTotalInclVat - item.depositTotalExclVat;

    return {
      productSKU: item.product.id.slice(0, SKU_MAX_LENGTH),
      productName,
      productDescription,
      unitPrice: minorUnitToMajorUnit(
        item.quantity > 0 ? lineTotal / item.quantity : 0
      ),
      quantity: item.quantity,
      totalAmount: minorUnitToMajorUnit(lineTotal),
      saleCode: TRANSACTION_CODE.SALE,
      taxAmount: minorUnitToMajorUnit(
        item.unitTotalPriceInclVat -
          roundAwayFromZero(item.unitTotalPriceExclVat) +
          depositTax
      ),
      taxIncludedFlag: true
    };
  });
}
